import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { theme } from '../theme';

import { API_BASE } from '../api/config';

const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;
document.head.appendChild(style);

const FloatingButton = React.memo(({ onOpen }) => (
    <div
        onClick={onOpen}
        style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '16px 24px',
            borderRadius: '50px',
            background: 'linear-gradient(135deg, #ff6b35 0%, #ff8555 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 8px 32px rgba(255, 107, 53, 0.4)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            border: '2px solid rgba(255,255,255,0.2)',
        }}
        onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(255, 107, 53, 0.6)';
        }}
        onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.4)';
        }}
        title="Open BRI AI Assistant"
    >
        <span style={{
            fontSize: '20px',
            fontWeight: '700',
            color: 'white',
            letterSpacing: '1px',
            fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
            textShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
            BRI AI
        </span>
    </div>
));

const ChatHeader = React.memo(({ isAdminRole, onClose }) => (
    <div style={{
        padding: '20px',
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, #1e293b 100%)`,
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }}>
        <div>
            <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                {isAdminRole ? 'BRI User Assistant' : 'BRI AI Assistant'}
            </div>
            <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                {isAdminRole ? 'User Management Agent' : 'Local Insights Agent'}
            </div>
        </div>
        <div
            onClick={onClose}
            style={{ cursor: 'pointer', fontSize: '20px' }}
        >
            ×
        </div>
    </div>
));

const MessagesList = React.memo(({ messages, loading, messagesEndRef }) => (
    <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
    }}>
        {messages.map((m, i) => (
            <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                padding: '12px 16px',
                borderRadius: '15px',
                backgroundColor: m.role === 'user' ? theme.colors.primary : 'rgba(255,255,255,0.05)',
                color: 'white',
                fontSize: '0.95rem',
                lineHeight: '1.4',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                border: m.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.1)',
                whiteSpace: 'pre-wrap'
            }}>
                {m.content}
            </div>
        ))}
        {loading && (
            <div style={{
                alignSelf: 'flex-start',
                padding: '12px 16px',
                borderRadius: '15px',
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: 'white',
                fontSize: '0.95rem',
                fontStyle: 'italic'
            }}>
                BRI is thinking...
            </div>
        )}
        <div ref={messagesEndRef} />
    </div>
));

const VoiceBanner = React.memo(({ isListening }) => {
    if (!isListening) return null;
    return (
        <div style={{
            padding: '14px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textAlign: 'center',
            fontSize: '15px',
            fontWeight: '600',
            fontFamily: "'Segoe UI', 'Arial', sans-serif",
            letterSpacing: '0.5px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            animation: 'pulse 1.5s ease-in-out infinite'
        }}>
             Listening... Speak now
        </div>
    );
});

const ChatInput = React.memo(({
    input,
    setInput,
    loading,
    isListening,
    isAdminRole,
    voiceEnabled,
    onToggleVoice,
    onSend,
    voiceSupported,
    onStartListening,
    onStopListening,
}) => (
    <div style={{
        padding: '20px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    }}>
        {voiceSupported ? (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                backgroundColor: isListening
                    ? 'rgba(102, 126, 234, 0.15)'
                    : 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                border: isListening
                    ? '2px solid rgba(102, 126, 234, 0.5)'
                    : '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.3s'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 15C13.66 15 15 13.66 15 12V6C15 4.34 13.66 3 12 3C10.34 3 9 4.34 9 6V12C9 13.66 10.34 15 12 15Z" fill={isListening ? '#667eea' : 'rgba(255,255,255,0.6)'}/>
                        <path d="M17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12H5C5 15.53 7.61 18.43 11 18.92V22H13V18.92C16.39 18.43 19 15.53 19 12H17Z" fill={isListening ? '#667eea' : 'rgba(255,255,255,0.6)'}/>
                    </svg>
                    <div>
                        <div style={{
                            color: '#fff',
                            fontSize: '15px',
                            fontWeight: '600',
                            fontFamily: "'Segoe UI', 'Arial', sans-serif",
                            letterSpacing: '0.3px'
                        }}>
                            Voice Assistant Mode
                        </div>
                        <div style={{
                            color: 'rgba(255,255,255,0.65)',
                            fontSize: '12px',
                            fontFamily: "'Segoe UI', 'Arial', sans-serif",
                            marginTop: '2px'
                        }}>
                            {isListening ? 'Active - Click to stop' : 'Click to start speaking'}
                        </div>
                    </div>
                </div>
                <button
                    onClick={isListening ? onStopListening : onStartListening}
                    disabled={loading}
                    style={{
                        background: isListening
                            ? 'linear-gradient(135deg, #ff4444 0%, #ff6666 100%)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50px',
                        padding: '10px 24px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        fontFamily: "'Segoe UI', 'Arial', sans-serif",
                        letterSpacing: '0.3px',
                        transition: 'all 0.2s',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                        opacity: loading ? 0.5 : 1
                    }}
                    onMouseEnter={e => !loading && (e.currentTarget.style.transform = 'scale(1.05)')}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                    {isListening ? 'Stop' : 'Start'}
                </button>
            </div>
        ) : null}

        <div style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
        }}>
            <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !loading && !isListening && onSend()}
                placeholder={isAdminRole ? "Ask about users, roles..." : "Ask about sales, trends..."}
                disabled={loading}
                style={{
                    flex: 1,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    padding: '12px 15px',
                    color: 'white',
                    outline: 'none',
                    fontSize: '14px',
                    fontFamily: "'Segoe UI', 'Arial', sans-serif"
                }}
            />
            <button
                onClick={onToggleVoice}
                style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: voiceEnabled
                        ? '1.5px solid #4caf50'
                        : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    padding: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '42px',
                    minHeight: '42px',
                    transition: 'all 0.2s'
                }}
                title={voiceEnabled ? 'Audio responses ON' : 'Audio responses OFF'}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {voiceEnabled ? (
                        <>
                            <path d="M3 9V15H7L12 20V4L7 9H3Z" fill="#4caf50"/>
                            <path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12Z" fill="#4caf50"/>
                            <path d="M14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z" fill="#4caf50"/>
                        </>
                    ) : (
                        <>
                            <path d="M3 9V15H7L12 20V4L7 9H3Z" fill="rgba(255,255,255,0.4)"/>
                            <line x1="16" y1="8" x2="22" y2="14" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round"/>
                            <line x1="22" y1="8" x2="16" y2="14" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round"/>
                        </>
                    )}
                </svg>
            </button>
            <button
                onClick={onSend}
                disabled={loading || isListening}
                style={{
                    backgroundColor: theme.colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px 24px',
                    cursor: (loading || isListening) ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    opacity: (loading || isListening) ? 0.6 : 1,
                    minHeight: '42px',
                    fontSize: '14px',
                    fontFamily: "'Segoe UI', 'Arial', sans-serif",
                    letterSpacing: '0.3px'
                }}
            >
                Send
            </button>
        </div>
    </div>
));

const AIAssistant = ({ filterContext, userRole }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isAdminRole = useMemo(() => userRole === 'IT_Admin' || userRole === 'Admin', [userRole]);
    const welcomeMessage = useMemo(() => {
        if (isAdminRole) {
            return 'Hi! I\'m BRI AI, your User Management Assistant. I can help you with information about users, roles, access levels, and system usage. How can I help you today?';
        }
        return 'Hi! I\'m BRI AI, your Sales Assistant. I can analyze the dashboard data, identify trends, and provide recommendations. How can I help you today?';
    }, [isAdminRole]);

    const [messages, setMessages] = useState([
        { role: 'assistant', content: welcomeMessage }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const messagesEndRef = useRef(null);
    const recognitionRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen, scrollToBottom]);

    useEffect(() => {
        console.log(' Checking speech recognition support...');
        console.log('webkitSpeechRecognition:', 'webkitSpeechRecognition' in window);
        console.log('SpeechRecognition:', 'SpeechRecognition' in window);

        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;
            recognitionRef.current.lang = 'en-US';

            console.log(' Speech recognition initialized successfully!');

            recognitionRef.current.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                console.log(' Transcript:', transcript);
                setInput(transcript);
                setIsListening(false);
            };

            recognitionRef.current.onerror = (event) => {
                console.error(' Speech recognition error:', event.error);
                setIsListening(false);

                if (event.error === 'not-allowed') {
                    alert(' Microphone access denied!\n\nPlease allow microphone access in your browser settings.\n\n1. Click the  lock icon in the address bar\n2. Allow microphone access\n3. Refresh the page');
                } else if (event.error === 'no-speech') {
                    alert(' No speech detected. Please try speaking again.');
                } else {
                    alert(' Speech recognition error: ' + event.error);
                }
            };

            recognitionRef.current.onend = () => {
                console.log(' Speech recognition ended');
                setIsListening(false);
            };
        } else {
            console.error(' Speech recognition NOT supported in this browser!');
        }
    }, []);

    const speakText = useCallback((text) => {
        if ('speechSynthesis' in window && voiceEnabled) {
            window.speechSynthesis.cancel();

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            utterance.volume = 1.0;

            window.speechSynthesis.speak(utterance);
        }
    }, [voiceEnabled]);

    const startListening = useCallback(() => {
        if (recognitionRef.current && !isListening) {
            try {
                recognitionRef.current.start();
                setIsListening(true);
            } catch (error) {
                console.error('Error starting speech recognition:', error);
                alert('Microphone error: ' + error.message + '\n\nPlease allow microphone access in your browser settings.');
            }
        } else if (!recognitionRef.current) {
            alert('Speech recognition is not supported in your browser.\n\nPlease use Chrome, Edge, or Safari.');
        }
    }, [isListening]);

    const stopListening = useCallback(() => {
        if (recognitionRef.current && isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        }
    }, [isListening]);

    const handleSendStreaming = useCallback(async (userMessage, salesperson, year, region) => {
        const loadingMessageId = Date.now();
        let fullResponse = '';

        const updateStreamingMessage = (content) => {
            setMessages(prev => prev.map(msg =>
                msg.id === loadingMessageId
                    ? { ...msg, content }
                    : msg
            ));
        };

        const finalizeStreamingMessage = (content) => {
            setMessages(prev => {
                const filtered = prev.filter(msg => !msg.id || msg.id !== loadingMessageId);
                return [...filtered, {
                    role: 'assistant',
                    content,
                    streaming: false
                }];
            });
        };

        setMessages(prev => [...prev, {
            role: 'assistant',
            content: '',
            id: loadingMessageId,
            streaming: true
        }]);

        try {
            console.log('AI Chat Request:', { salesperson, year, region, message: userMessage.substring(0, 50) });

            const response = await fetch(`${API_BASE}/ai/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    message: userMessage,
                    salesperson: salesperson || "Unknown",
                    year: parseInt(year) || 2025,
                    region: region || "all",
                })
            });

            if (!response.ok) {
                let errorDetail = `HTTP error! status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorDetail = errorData.detail || errorData.error || errorDetail;
                } catch (e) {
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);

                        if (data === '[DONE]') {
                            finalizeStreamingMessage(fullResponse);
                            if (fullResponse) {
                                speakText(fullResponse);
                            }
                            return;
                        }

                        try {
                            const parsed = JSON.parse(data);
                            if (parsed.content) {
                                if (parsed.content.startsWith('[ERROR:')) {
                                    throw new Error(parsed.content);
                                }
                                fullResponse += parsed.content;

                                updateStreamingMessage(fullResponse);
                            } else if (parsed.error) {
                                throw new Error(parsed.error);
                            }
                        } catch (e) {
                            if (e instanceof SyntaxError) continue;
                            if (e instanceof Error && e.message.includes('[ERROR:')) {
                                throw e;
                            }
                            throw e;
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Streaming error:", error);
            const errorMessage = error.message || "Error receiving streaming response. Please try again.";
            setMessages(prev => {
                const filtered = prev.filter(msg => !msg.id || msg.id !== loadingMessageId);
                return [...filtered, {
                    role: 'assistant',
                    content: fullResponse || errorMessage,
                    streaming: false
                }];
            });
        }
    }, [speakText]);

    const handleSendUserStreaming = useCallback(async (userMessage) => {
        const loadingMessageId = Date.now();
        let fullResponse = '';

        const updateStreamingMessage = (content) => {
            setMessages(prev => prev.map(msg =>
                msg.id === loadingMessageId
                    ? { ...msg, content }
                    : msg
            ));
        };

        const finalizeStreamingMessage = (content) => {
            setMessages(prev => {
                const filtered = prev.filter(msg => !msg.id || msg.id !== loadingMessageId);
                return [...filtered, {
                    role: 'assistant',
                    content,
                    streaming: false
                }];
            });
        };

        setMessages(prev => [...prev, {
            role: 'assistant',
            content: '',
            id: loadingMessageId,
            streaming: true
        }]);

        try {
            const response = await fetch(`${API_BASE}/api/users/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ message: userMessage })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);

                        if (data === '[DONE]') {
                            finalizeStreamingMessage(fullResponse);
                            if (fullResponse) {
                                speakText(fullResponse);
                            }
                            return true;
                        }

                        try {
                            const parsed = JSON.parse(data);
                            if (parsed.content) {
                                fullResponse += parsed.content;
                                updateStreamingMessage(fullResponse);
                            } else if (parsed.error) {
                                throw new Error(parsed.error);
                            }
                        } catch (e) {
                            if (e instanceof SyntaxError) continue;
                            throw e;
                        }
                    }
                }
            }

            finalizeStreamingMessage(fullResponse);
            if (fullResponse) {
                speakText(fullResponse);
            }
            return true;
        } catch (error) {
            console.error("User chat streaming error:", error);
            setMessages(prev => prev.filter(msg => !msg.id || msg.id !== loadingMessageId));
            return false;
        }
    }, [speakText]);

    const handleSend = useCallback(async () => {
        if (!input.trim() || loading) return;

        const userMessage = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setLoading(true);

        try {
            if (userRole === 'IT_Admin') {
                const streamed = await handleSendUserStreaming(userMessage);
                if (!streamed) {
                    const response = await axios.post(`${API_BASE}/api/users/chat`, {
                        message: userMessage
                    }, {
                        timeout: 120000
                    });

                    setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
                    speakText(response.data.response);
                }
                setLoading(false);
                return;
            }

            if (userRole === 'Admin') {
                const fuzzyMatch = (text, keyword, maxTypos = 2) => {
                    const textLower = text.toLowerCase();
                    const keywordLower = keyword.toLowerCase();

                    if (textLower.includes(keywordLower)) return true;

                    const words = textLower.split(/\s+/);
                    for (const word of words) {
                        if (word.length >= keywordLower.length - maxTypos &&
                            word.length <= keywordLower.length + maxTypos) {
                            if (word.substring(0, Math.min(4, keywordLower.length)) ===
                                keywordLower.substring(0, Math.min(4, word.length))) {
                                return true;
                            }
                            if (keywordLower.length >= 4 && word.includes(keywordLower.substring(0, 4))) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                const msgLower = userMessage.toLowerCase();

                const hasForecast = fuzzyMatch(userMessage, 'forecast') ||
                                   fuzzyMatch(userMessage, 'forecasted') ||
                                   msgLower.includes('booking') || msgLower.includes('bookings');
                const hasFOC = /foc/i.test(userMessage) || msgLower.includes('sample');
                const hasHighPriority = /high priority|priority enquiries|priority enquiry/i.test(userMessage);
                const hasConcerning = /concerning enquiries|concerning enquiry|concerning/i.test(userMessage);
                const hasSalesTerms = ['closed lost', 'closed lost reason', 'closed lost reasons', 'lost reason', 'lost reasons', 'list closed lost',
                    'companies', 'company', 'pipeline', 'pipeline overview', 'sales',
                    'opportunities', 'opportunity', 'deals', 'deal', 'revenue', 'margin',
                    'year comparison', 'monthly performance', 'year wise', 'enquiries', 'enquiry',
                    'stage', 'stages', 'quote sent', 'estimation', 'boq', 'probability', 'win rate',
                    'win percentage', 'winpercent', 'wind rate', 'win probability', 'closed won', 'closed lost'].some(term =>
                    fuzzyMatch(userMessage, term) || msgLower.includes(term));

                const hasTopCompanies = /top\s+compan/i.test(userMessage) ||
                                       (msgLower.includes('top') && (msgLower.includes('compan') || msgLower.includes('companie')));

                const hasSalesPattern = /top\s+\d+\s+(forecast|forecasrted|booking|opportunit|deal|compan)/i.test(userMessage) ||
                    /(forecast|forecasrted|booking|opportunit).*(top|list|show|tell)/i.test(userMessage) ||
                    /tell.*(about|forecast|forecasrted|booking|opportunit)/i.test(userMessage);

                const isSalesQuery = hasForecast || hasFOC || hasHighPriority || hasConcerning || hasTopCompanies || hasSalesTerms || hasSalesPattern;

                console.log('Query detection:', {
                    userMessage,
                    isSalesQuery,
                    hasSalesTerms,
                    hasForecast,
                    hasFOC,
                    filterContext
                });

                if (isSalesQuery) {
                    const { salesperson, year, region } = filterContext || {};

                    if (!salesperson || !year) {
                        setMessages(prev => [...prev, {
                            role: 'assistant',
                            content: 'To query sales data, please select a salesperson and year from the filters above, then ask your question again.'
                        }]);
                        setLoading(false);
                        return;
                    }

                    console.log('Routing to sales endpoint:', { salesperson, year, region });
                    await handleSendStreaming(userMessage, salesperson, year, region);
                    setLoading(false);
                    return;
                } else {
                    console.log('Routing to user management endpoint');
                    const streamed = await handleSendUserStreaming(userMessage);
                    if (!streamed) {
                        const response = await axios.post(`${API_BASE}/api/users/chat`, {
                            message: userMessage
                        }, {
                            timeout: 120000
                        });

                        setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
                        speakText(response.data.response);
                    }
                    setLoading(false);
                    return;
                }
            }


            const { salesperson, year, region } = filterContext || {};
            const useStreaming = true;

            if (useStreaming) {
                await handleSendStreaming(userMessage, salesperson, year, region);
            } else {
                const response = await axios.post(`${API_BASE}/ai/chat`, {
                    message: userMessage,
                    salesperson: salesperson || "Unknown",
                    year: parseInt(year) || 2025,
                    region: region || "all",
                    stream: false
                }, {
                    timeout: 120000
                });

                setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
                speakText(response.data.response);
            }
        } catch (error) {
            console.error("AI Error:", error);
            let errorMessage = "I'm having trouble reaching my brain right now.";

            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMessage = "Request timed out. The AI model may be loading (first request takes 30-60 seconds). Please try again.";
            } else if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response?.data?.response) {
                errorMessage = error.response.data.response;
            } else if (error.message) {
                errorMessage = `Error: ${error.message}`;
            }

            setMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
        } finally {
            setLoading(false);
        }
    }, [input, loading, isAdminRole, filterContext, handleSendStreaming, handleSendUserStreaming, speakText]);

    const handleOpen = useCallback(() => setIsOpen(true), []);
    const handleClose = useCallback(() => setIsOpen(false), []);
    const handleToggleVoice = useCallback(() => setVoiceEnabled(prev => !prev), []);
    const voiceSupported = useMemo(() => (
        'webkitSpeechRecognition' in window || 'SpeechRecognition' in window
    ), []);

    if (!isOpen) {
        return <FloatingButton onOpen={handleOpen} />;
    }

    return (
        <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '380px',
            height: '550px',
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.1)',
            zIndex: 1000,
            overflow: 'hidden'
        }}>
            <ChatHeader isAdminRole={isAdminRole} onClose={handleClose} />
            <MessagesList messages={messages} loading={loading} messagesEndRef={messagesEndRef} />
            <VoiceBanner isListening={isListening} />
            <ChatInput
                input={input}
                setInput={setInput}
                loading={loading}
                isListening={isListening}
                isAdminRole={isAdminRole}
                voiceEnabled={voiceEnabled}
                onToggleVoice={handleToggleVoice}
                onSend={handleSend}
                voiceSupported={voiceSupported}
                onStartListening={startListening}
                onStopListening={stopListening}
            />
        </div>
    );
};

export default AIAssistant;
